"use client";
import React, { useCallback, useEffect, useState } from "react";
import Icon from "@/components/icons/Icons";
import CustomModal from "@/components/modal/CustomModal";
import { useLoginModal } from "@/utils/useLoginModal";
import { apiGet, apiPost } from "@/utils/apiClient";
import { addToast } from "@/components/ui/CustomToastManager";

interface Watchlist {
  id: number;
  watchlist_name: string | null;
  fundsList: number[];
}

type Prop = {
  schemeId?: number;
  categoryName?: string | null;
};

export default function SavedFundButton({ schemeId, categoryName }: Prop) {
  const [watchlists, setWatchlists] = useState<Watchlist[]>([]);
  const [isSaved, setIsSaved] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [createNew, setCreateNew] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [error, setError] = useState<{ createCategory?: string; updateList?: string }>({});

  const { open, isLoggedIn, setModalKey } = useLoginModal();

  const createWatchlist = useCallback(async (name: string) => {
    if (!name) return;
    try {
      const res = await apiPost("watchlistCreate", { watchlist_name: name });
      return res.status === 1;
    } catch (err) {
      console.error("Error creating watchlist:", err);
      return false;
    }
  }, []);

  const fetchWatchlist = useCallback(async () => {
    if (!schemeId || !isLoggedIn) return;
    try {
      const res = await apiGet("watchlist");
      const fetched: Watchlist[] = res.result || [];

      if (fetched.length <= 0) {
        const success = await createWatchlist(categoryName || "Default");
        if (success) {
          setTimeout(() => {
            fetchWatchlist();
          }, 300);
        }
        return;
      }

      setWatchlists(fetched);

      const match = fetched.find((wl) => wl.fundsList.includes(schemeId));
      setIsSaved(!!match);
      setSelectedCategory((prev) => match?.watchlist_name ?? prev ?? fetched[0]?.watchlist_name ?? "Default Watchlist");
    } catch (err) {
      console.error("Failed to fetch watchlist:", err);
    }
  }, [schemeId, categoryName, createWatchlist]);

  const updateWatchlist = async (name: string, type: "add" | "remove") => {
    const match = watchlists.find((w) => w.watchlist_name === name);
    if (!match || !schemeId) return false;
    try {
      const payload = {
        watchlist_id: match.id,
        watchlist_name: match.watchlist_name ?? watchlists[0].watchlist_name,
        sch_id: schemeId,
        type,
      };

      const res = await apiPost("watchlistUpdate", payload);
      return res.status === 1;
    } catch (err) {
      console.error("Error updating watchlist:", err);
      return false;
    }
  };

  useEffect(() => {
    fetchWatchlist();
  }, [fetchWatchlist,isLoggedIn]);

  const handleCreateCategory = async () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) return setError({ createCategory: "Category name required." });
    if (watchlists.some((w) => w.watchlist_name === trimmed)) return setError({ createCategory: "Category already exists." });
    const success = await createWatchlist(trimmed);
    if (success) {
      await fetchWatchlist();
      setSelectedCategory(trimmed);
      setCreateNew(false);
      setNewCategoryName("");
    } else {
      setError({ createCategory: "Failed to create category." });
    }
  };

  const handleUpdateList = async () => {
    const finalName = createNew ? newCategoryName.trim() : selectedCategory || categoryName || "Default Watchlist";
    const type = isSaved ? "remove" : "add";
    const success = await updateWatchlist(finalName, type);
    if (success) {
      setIsSaved(!isSaved);
      addToast({
        title: "Watchlist Updated",
        description: isSaved ? "Item removed from your watchlist." : "Item added to your watchlist.",
        status: "success",
      });
      handleClose();
      fetchWatchlist();
    } else {
      addToast({
        title: "Update Failed",
        description: "Failed to update your watchlist.",
        status: "failed",
      });
      setError({ updateList: "Failed to update watchlist." });
    }
  };

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setCreateNew(false);
    setNewCategoryName("");
    setSelectedCategory(null);
    setError({});
  }, []);

  const handleClick = () => {
    if (isLoggedIn) {
      if (isSaved) {
        handleUpdateList();
      } else {
        setIsOpen(true);
        setSelectedCategory(watchlists[0].watchlist_name);
      }
    } else {
      open();
      setModalKey("bookmark-scheme-detail-page");
    }
  };

  return (
    <>
      <button onClick={handleClick} aria-label="Save Fund Button">
        {isSaved ? <Icon.savedFundIcon className="text-primaryBlue hover:text-[#376ec7]" /> : <Icon.unSavedFundIcon className="text-[#6E6E73] hover:text-primaryBlack" />}
      </button>

      <CustomModal isOpen={isOpen} onClose={handleClose} maxSize="max-w-500" className="bg-white rounded-4xl p-6 flex flex-col h-93">
        <p className="text-lg mb-4 font-semibold">
          {createNew ? (
            <span className="flex items-center gap-2">
              <button
                onClick={() => {
                  setCreateNew(false);
                  setSelectedCategory(watchlists[0].watchlist_name);
                }}
                className="text-xl font-bold"
              >
                ←
              </button>
              Enter a New Category
            </span>
          ) : (
            "Add Fund to Watchlist"
          )}
        </p>

        {!createNew && (
          <div className="space-y-2">
            <p className="text-sm text-gray-500">Select a category:</p>
            <div className="flex flex-col gap-3 max-h-200 overflow-y-auto scrollbar-none">
              {watchlists.map(({ id, watchlist_name }) => {
                const name = watchlist_name ?? "Default Watchlist";
                const isActive = selectedCategory === name;
                return (
                  <label key={id} className={`flex items-center gap-2 border rounded-xl px-4 py-2.5 transition duration-300 ease-in-out cursor-pointer ${isActive ? "border-primaryBlue bg-[#1A51AA24]" : "border-[#2020201c] bg-white hover:bg-[#1A51AA24]"}`}>
                    <input type="radio" name="watchlist" value={name} checked={isActive} onChange={() => setSelectedCategory(name)} />
                    {name}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {createNew && (
          <>
            <label className="text-sm text-gray-500">Enter a new category name:</label>
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => {
                setNewCategoryName(e.target.value);
                if (error.createCategory) setError((prev) => ({ ...prev, createCategory: "" }));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl mt-5"
              placeholder="Enter The Category Name"
            />

            {error.createCategory && <p className="text-red-500 text-sm mt-1">{error.createCategory}</p>}
            <button onClick={handleCreateCategory} className="bg-primaryBlue text-white px-6 py-2 rounded-full mt-auto">
              Add
            </button>
          </>
        )}

        {error.updateList && <p className="text-red-500 text-sm mt-2">{error.updateList}</p>}

        {!createNew && (
          <div className="flex justify-end gap-2.5 items-center mt-auto">
            <button
              onClick={() => {
                setCreateNew(true);
                setSelectedCategory(null);
              }}
              className="text-sm text-primaryBlue border border-primaryBlue rounded-full px-6 py-2 flex items-center gap-1"
            >
              <span>＋</span> Create A New Category
            </button>
            <button onClick={handleUpdateList} className="text-sm bg-primaryBlue text-white px-6 py-2 rounded-full">
              Save
            </button>
          </div>
        )}
      </CustomModal>
    </>
  );
}
